import { memo, useMemo, useState, useCallback, Dispatch, SetStateAction } from 'react'
import sameDay from 'date-fns/isSameDay'
import subDays from 'date-fns/subDays'
import { getDayStart, getDayEnd, buildTimeSlots } from 'components/common/Month/Tools'
import { useTranslation } from 'services/Translation'
import Typography from 'components/common/Typography'
import Separator from 'components/common/Separator'
import TextInput from 'components/common/inputs/Text'
import FloatingSelectInput from 'components/common/inputs/SelectFloating'
import DefaultBtn from 'components/common/buttons/Default/Component'
import usePoll, { alreadyStartedLoops } from 'hooks/usePoll'
import { getAccessTokenAndClaims } from 'services/Authentication'
import { useTimeOffState } from 'services/TimeOff'
import {
  useAppointmentsDispatch,
  useAppointmentsState,
  getAll as getAppointments,
} from 'services/Appointments'
import { textPrimary, error } from 'styles/Colors'
import { ROUTES } from 'Constants'
import { hp } from 'styles/Sizes'
import { Step, TimeWindow } from '../Types'
import { FooterSchedule } from './Styles'
import CalendarCard from './CalendarCard'

const REFRESH_DOCTOR_APPOINTMENT = parseInt(
  process.env.REACT_APP_REFRESH_DOCTOR_APPOINTMENT || '30000', // Increased from 5s to 30s
  10
)

const durationOptions = [5, 10, 15, 20, 25, 30]

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    title: getText('Scheduled AV request', 'Patient_Details_Modal_Select_Form'),
    durationLabel: getText('Appointment duration :', 'Patient_Details_Modal_Select_Form'),
    windowLabel: getText('Appointment window :', 'Patient_Details_Modal_Select_Form'),
    afterWindow: getText('After :', 'Patient_Details_Modal_Select_Form'),
    beforeWindow: getText('Before :', 'Patient_Details_Modal_Select_Form'),
    atWindow: getText('At :', 'Patient_Details_Modal_Select_Form'),
    notesLabel: getText(
      'Please type if any note for the patient',
      'Patient_Details_Modal_Select_Form'
    ),
    notesPlaceholder: getText('Write here', 'Patient_Details_Modal_Select_Form'),
    selectCalendar: getText('Select a date', 'Patient_Details_Modal_Select_Form'),
    textAV: getText(
      'If you send out a scheduled request, he or she will receive a choice menu to pick a slot according to your availability schedule.',
      'Patient_Details_Modal_Select_Form'
    ),
    subtext: getText(
      'Please make sure your availability schedule is properly planned.',
      'Patient_Details_Modal_Select_Form'
    ),
    dateErr: getText(
      'No slots available for the selected window',
      'Patient_Details_Modal_Select_Form'
    ),
    dateWarn1: getText('Warning: only', 'Patient_Details_Modal_Select_Form'),
    dateWarn2: getText('slots left in the select window', 'Patient_Details_Modal_Select_Form'),
    btnLabelNext: getText('Next', 'Patient_Details_Modal_Select_Form'),
    dateSelectionHelp: getText('Select one date for a specific day, or two dates to define a time window', 'Patient_Details_Modal_Select_Form'),
    selectedDateWindow: getText('Selected Date Window:', 'Patient_Details_Modal_Select_Form'),
    noDatesSelected: getText('No dates selected', 'Patient_Details_Modal_Select_Form'),
  }[key])



interface SelectProps {
  notes: string
  setNotes: Dispatch<SetStateAction<string>>
  validTimeWindow: TimeWindow
  setValidTimeWindow: Dispatch<SetStateAction<TimeWindow>>
  duration: number
  setDuration: Dispatch<SetStateAction<number>>
  setStep: Dispatch<SetStateAction<Step>>
}



const Select = ({
  notes,
  setNotes,
  validTimeWindow,
  setValidTimeWindow,
  duration,
  setDuration,
  setStep,
}: SelectProps) => {
  const { t, formatDate: format } = useTranslation(texts)
  const { token, claims } = getAccessTokenAndClaims()

  const [dateError, setDateError] = useState<string | null>(null)
  const [dateWarn, setDateWarn] = useState<string | null>(null)

  const handleNotes = useCallback(
    (newVal: string) => {
      setNotes(newVal)
    },
    [setNotes]
  )
  const handleDuration = useCallback(
    (selectedDurationId: number) => {
      setDuration(durationOptions[selectedDurationId])
    },
    [setDuration]
  )

  const appointmentsDispatch = useAppointmentsDispatch()
  const appointmentsInterval = useCallback(async () => {
    const alreadyStarted = alreadyStartedLoops as any
    if (alreadyStarted.patientsDetailsAppointmentInterval) {
      await getAppointments({ token }, { staffId: null, appointmentsDispatch })
    }
  }, [appointmentsDispatch, token])

  usePoll(appointmentsInterval, REFRESH_DOCTOR_APPOINTMENT, {
    contextName: 'patientsDetailsAppointmentInterval',
    isRunning: Boolean(token && window.location.pathname.startsWith(ROUTES.PATIENT)),
  })

  const { list: appointments } = useAppointmentsState()
  const { list: rawTimeOffs } = useTimeOffState()
  const timeOffs = useMemo(() => (claims?.sub ? rawTimeOffs?.[claims.sub] : null) || [], [
    rawTimeOffs,
    claims,
  ])
  const events = useMemo(() => [...timeOffs, ...appointments], [timeOffs, appointments])

  const handleConfirmAppointment = useCallback(() => {
    console.log('🔥 BUTTON CLICKED! handleConfirmAppointment called', { validTimeWindow, events, duration })
    
    // Clear previous errors
    setDateError(null)
    setDateWarn(null)
    
    const finalTimeSlots = buildTimeSlots(validTimeWindow, events, duration)
    console.log('finalTimeSlots', finalTimeSlots)
    
    const slotsLength = finalTimeSlots.reduce((acc, curr) => acc + curr?.slots?.length || 0, 0)
    console.log('slotsLength', slotsLength)
    
    if (slotsLength === 0) {
      setDateError(t('dateErr'))
      return
    }
    if (!dateWarn && slotsLength <= 5) {
      setDateWarn(`${t('dateWarn1')} ${slotsLength} ${t('dateWarn2')}`)
      return
    }

    console.log('Moving to next step')
    setStep(Step.AppointmentCreation)
  }, [setStep, validTimeWindow, events, duration, t, dateWarn])





  // New date selection logic for calendar-based selection
  const handleDateSelection = useCallback((selectedDate: Date) => {
    console.log('handleDateSelection called with:', selectedDate)
    console.log('Current validTimeWindow:', validTimeWindow)
    
    if (!validTimeWindow.start && !validTimeWindow.end) {
      // First date selection
      console.log('First date selection')
      setValidTimeWindow({
        start: getDayStart(selectedDate),
        end: getDayEnd(selectedDate),
      })
    } else if (validTimeWindow.start && validTimeWindow.end && sameDay(validTimeWindow.start, validTimeWindow.end)) {
      // Currently have single date, selecting second date for range
      const firstDate = validTimeWindow.start
      if (sameDay(firstDate, selectedDate)) {
        // Clicking same date - deselect
        console.log('Deselecting same date')
        setValidTimeWindow({ start: null, end: null })
      } else {
        // Create range
        console.log('Creating range')
        const startDate = selectedDate < firstDate ? selectedDate : firstDate
        const endDate = selectedDate > firstDate ? selectedDate : firstDate
        setValidTimeWindow({
          start: getDayStart(startDate),
          end: getDayEnd(endDate),
        })
      }
    } else if (validTimeWindow.start && validTimeWindow.end) {
      // Currently have range, selecting new single date
      if (
        (validTimeWindow.start && sameDay(validTimeWindow.start, selectedDate)) ||
        (validTimeWindow.end && sameDay(validTimeWindow.end, selectedDate))
      ) {
        // Clicking on one of the range dates - deselect all
        console.log('Deselecting range')
        setValidTimeWindow({ start: null, end: null })
      } else {
        // Select new single date
        console.log('Selecting new single date')
        setValidTimeWindow({
          start: getDayStart(selectedDate),
          end: getDayEnd(selectedDate),
        })
      }
    }
  }, [validTimeWindow])

  const getSelectedDates = useCallback(() => {
    const dates = []
    if (validTimeWindow.start) dates.push(validTimeWindow.start)
    if (validTimeWindow.end && !sameDay(validTimeWindow.start || new Date(), validTimeWindow.end)) {
      dates.push(validTimeWindow.end)
    }
    return dates
  }, [validTimeWindow])

  const getDateWindowText = useCallback(() => {
    if (!validTimeWindow.start && !validTimeWindow.end) {
      return t('noDatesSelected')
    }
    
    if (validTimeWindow.start && validTimeWindow.end) {
      if (sameDay(validTimeWindow.start, validTimeWindow.end)) {
        // Single date selected
        return format(validTimeWindow.start, 'dd MMMM, yyyy')
      }
      // Date range selected
      return `${format(validTimeWindow.start, 'dd MMMM, yyyy')} - ${format(validTimeWindow.end, 'dd MMMM, yyyy')}`
    }
    
    return t('noDatesSelected')
  }, [validTimeWindow, format, t])

  // Debug log for button state
  console.log('Button disabled state:', !validTimeWindow.start, 'validTimeWindow:', validTimeWindow)

  return (
    <>
      <div className="flex flex-col text-lg py-3 mb-3">
        <Typography
          type="title"
          className="font-csc65"
          text={t('title')}
          color={textPrimary}
          size={18}
          style={undefined}
        />
        <Separator size={hp(3)} />
        <Typography
          className="font-csc45 text-medium"
          text={t('textAV')}
          color={textPrimary}
          size={15}
          style={undefined}
        />
        <Separator size={hp(3)} />
        <Typography
          type="title"
          italic
          className="font-csc65"
          text={t('subtext')}
          color={textPrimary}
          size={15}
          style={undefined}
        />
        <Separator size={hp(2.5)} />
        <div style={{ opacity: 0.4, pointerEvents: 'none' }}>
          <Typography
            type="title"
            className="font-csc65"
            text={t('notesLabel')}
            size={15}
            style={{ color: '#999999' }}
          />
          <Separator size={hp(1)} />
          <TextInput
            field={{
              onChange: ({ target: { value } }: any) => handleNotes(value),
              value: notes,
            }}
            noBorder
            multiline
            placeholder={t('notesPlaceholder')}
            noPadding
            name={undefined}
            style={{ opacity: 0.6 }}
            label={undefined}
            inputRef={undefined}
            inputStyle={undefined}
            containerStyle={undefined}
            placeholderStyle={undefined}
            data-cy={undefined}
          />
        </div>
        <Separator size={hp(2)} />
        <div className="flex items-center" style={{ opacity: 0.4, pointerEvents: 'none' }}>
          <Typography
            type="title"
            className="font-csc65 mr-6"
            text={t('durationLabel')}
            size={15}
            style={{ color: '#999999' }}
          />
          <FloatingSelectInput
            field={{
              onChange: handleDuration,
              value: durationOptions.findIndex(e => e === duration),
            }}
            size="0.75rem"
            options={durationOptions.map(value => `${value} minutes`)}
            label={undefined}
            style={{ opacity: 0.6 }}
            data-cy={undefined}
          />
        </div>
        <Separator size={hp(3)} />
        <Typography
          type="title"
          className="font-csc65"
          text={t('windowLabel')}
          size={15}
          style={undefined}
        />
        <Separator size={hp(2)} />
        <Typography
          className="font-csc45 text-center"
          text={t('dateSelectionHelp')}
          color={textPrimary}
          size={13}
          style={undefined}
        />
        <Separator size={hp(2)} />
        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'baseline', gap: '16px' }}>
          <Typography
            className="font-csc65"
            text={t('selectedDateWindow')}
            color={textPrimary}
            size={14}
            style={{ textAlign: 'left' }}
          />
          <Typography
            className="font-csc45"
            text={getDateWindowText()}
            color="#EB7C0C"
            size={14}
            style={{ 
              minHeight: '1.5rem',
              fontWeight: 'bold',
              textAlign: 'left'
            }}
          />
        </div>
        <Separator size={hp(2)} />
        <div style={{ marginTop: '10px' }} className="flex justify-start">
          <CalendarCard
            field={{
              value: null, // We'll handle selection differently
              onChange: handleDateSelection,
            }}
            events={events}
            appointmentDuration={duration}
            disableBefore={subDays(new Date(), 1)}
            selectedDates={getSelectedDates()}
            isRangeSelection
          />
        </div>


      </div>
      <Separator size={hp(8)} />
      <FooterSchedule>
        {dateError !== null && (
          <Typography
            italic
            className="font-csc45"
            center
            text={dateError}
            color={error}
            size={undefined}
            style={undefined}
          />
        )}
        {dateWarn !== null && (
          <Typography
            italic
            className="font-csc45"
            center
            text={dateWarn}
            color={error}
            size={undefined}
            style={undefined}
          />
        )}
        <div 
          className="w-full flex justify-center align-center border-t border-grey-100 pt-12"
          style={{ position: 'relative', zIndex: 1000 }}
          onMouseEnter={() => console.log('Mouse entered button area')}
          onMouseLeave={() => console.log('Mouse left button area')}
        >
          <DefaultBtn
            label={t('btnLabelNext')}
            onClick={handleConfirmAppointment}
            type="submit"
            className="font-csc65"
            reversedColors
            color={textPrimary}
            style={{ 
              width: '9.375rem', 
              position: 'relative', 
              zIndex: 1001,
              pointerEvents: 'auto'
            }}
            disabled={!validTimeWindow.start}
            nextIcon={undefined}
          />
        </div>
      </FooterSchedule>
    </>
  )
}

Select.whyDidYouRender = true

export default memo(Select)
